import * as React from 'react';
import { styled } from '@mui/material/styles';
import MuiTextField, { type TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Colors from '../../Themes/Colors';

// Bootstrap-style MUI TextField
export const StyledTextField = styled(MuiTextField)({
  '& .MuiInputLabel-root': {
    color: Colors.formLabel,
    fontSize: '14px',
    fontWeight: 400,
    transform: 'none',
    position: 'static',
    marginBottom: '4px',
    pointerEvents: 'auto',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: Colors.formLabel,
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: Colors.formError,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: Colors.white,
    borderRadius: '4px',
    fontSize: '14px',
    color: Colors.textInputText,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ced4da',
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#adb5bd',
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#86b7fe',
      borderWidth: '1px',
      boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)',
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: Colors.formError,
    },
    '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: Colors.formError,
      boxShadow: `0 0 0 0.25rem rgba(220, 53, 69, 0.25)`,
    },
    '&.Mui-disabled': {
      backgroundColor: '#fafafa',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e8eaed',
      },
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
    lineHeight: '1.5',
    '&::placeholder': {
      color: Colors.textInputPlaceholder,
      opacity: 1,
    },
    '&.Mui-disabled': {
      WebkitTextFillColor: Colors.textInputTextDisabled,
      textOverflow: 'ellipsis',
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: '4px',
    fontSize: '12px',
    color: Colors.formLabel,
    '&.Mui-error': {
      color: Colors.formError,
    },
  },
  // Remove the floating label notch gap
  '& .MuiOutlinedInput-notchedOutline legend': {
    width: 0,
  },
});

type BaseTextFieldProps = Omit<MuiTextFieldProps, 'onChange' | 'error' | 'variant'>;

export interface OutlinedTextFieldProps extends BaseTextFieldProps {
  /** Callback with just the value string */
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Error message string (also sets error state) */
  error?: string;
  /** Placeholder text */
  hint?: string;
  /** Icon to display as start adornment */
  icon?: React.ReactNode;
  /** Node to display as end adornment — remains clickable even when the field is disabled */
  endAdornment?: React.ReactNode;
  /** Max length of input */
  maxLength?: number;
  /** Inner ref for accessing the input element */
  innerRef?: React.Ref<HTMLInputElement>;
}

const OutlinedTextField = React.forwardRef<HTMLDivElement, OutlinedTextFieldProps>(
  ({ onChange, error, hint, icon, endAdornment, innerRef, maxLength, slotProps, fullWidth = true, ...rest }, ref) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event.target.value, event);
      }
    };

    const inputSlotProps: MuiTextFieldProps['slotProps'] = { ...slotProps };

    if (icon || endAdornment) {
      inputSlotProps.input = {
        ...slotProps?.input,
        ...(icon ? { startAdornment: <InputAdornment position="start">{icon}</InputAdornment> } : {}),
        ...(endAdornment
          ? {
              endAdornment: (
                <InputAdornment position="end" sx={{ pointerEvents: 'auto' }}>
                  {endAdornment}
                </InputAdornment>
              ),
            }
          : {}),
      };
    }

    if (maxLength) {
      inputSlotProps.htmlInput = {
        ...slotProps?.htmlInput,
        maxLength,
      };
    }

    // Prevent the label from shrinking/floating — Bootstrap labels are always static
    inputSlotProps.inputLabel = {
      ...slotProps?.inputLabel,
      shrink: true,
    };

    return (
      <StyledTextField
        ref={ref}
        inputRef={innerRef}
        onChange={handleChange}
        error={Boolean(error)}
        helperText={error || undefined}
        placeholder={hint}
        slotProps={inputSlotProps}
        variant="outlined"
        fullWidth={fullWidth}
        {...rest}
      />
    );
  },
);

OutlinedTextField.displayName = 'OutlinedTextField';

export default OutlinedTextField;
